import classNames from "classnames";
import { useContext, useEffect, useRef, useState } from "react";

import MobileHeader from "src/app/components/datatable/header/MobileHeader";
import TableHeader from "src/app/components/datatable/header/TableHeader";
import Loader from "src/app/components/loader/Loader";
import adminCSS from "src/app/modules/admin/Admin.module.css";
import { PROJECT_TABS } from "src/app/modules/admin/components/projects/constants";
import { TARGET_GROUPS } from "src/app/modules/auth/constants";
import projectCSS from "src/app/modules/user-projects/Projects.module.css";
import { ModalContext } from "src/contexts/ModalContext";
import { ApiResult } from "src/core/helpers/api/ApiResult";
import { Column } from "src/core/models/global";
import { PartnerResponse } from "src/core/models/partners/PartnerResponse";
import { PartnersService } from "src/core/services/partners";
import { TableHead } from "src/types/global";

const headers: Array<TableHead> = [
  {
    name: "Partner",
    field: "name",
    sortable: true,
    class: "u-col-7 u-t-secondary u-t-uppercase u-t-align-start u-cursor-pointer u-t-f16p",
    mclass: "u-mb20px u-t-f16p u-t-lh15",
  },
  {
    name: "Broj Upita",
    field: "projects",
    sortable: true,
    class: "u-col-5 u-t-secondary u-t-uppercase u-t-align-start u-cursor-pointer u-t-f16p u-t-align-end",
    mclass: "u-mb20px u-t-f16p u-t-lh15",
  },
];

const Statistics = ({
  activeTab,
  filteredPartnerList,
  filteredRegionList,
  filteredCityList,
  filteredSystemList,
  filteredTargetGroupList,
  filteredDateFrom,
  filteredDateTo,
}: {
  activeTab: string;
  filteredPartnerList: Array<number> | null;
  filteredRegionList: Array<number> | null;
  filteredCityList: Array<number> | null;
  filteredSystemList: Array<number> | null;
  filteredTargetGroupList: Array<number> | null;
  filteredDateFrom: Date | undefined;
  filteredDateTo: Date | undefined;
}) => {
  const modalData = useContext(ModalContext);

  const [partnerStats, setPartnerStats] = useState<Array<PartnerResponse>>([]);
  const [loading, setLoading] = useState(true);
  const [sorting, setSorting] = useState<Column>({ name: "projects", sortDescending: true });

  const [headerMenuActive, setHeaderMenuActive] = useState(false);
  const headerMenuRef = useRef<Array<any>>([]);
  const setHeaderMenuRef = (key: number, ref: any) => {
    headerMenuRef.current[key] = ref;
  };

  const sortTable = (sortColumn: Column) => {
    setLoading(true);
    setSorting(sortColumn);
    const partners = [...partnerStats.map((x) => ({ ...x }))];
    let sortedPartners: Array<PartnerResponse> = [];
    if (sortColumn.sortDescending) {
      if (sortColumn.name === "name") {
        sortedPartners = partners.sort((a: PartnerResponse, b: PartnerResponse) => (a.name && b.name && a.name.toLowerCase() < b.name.toLowerCase() ? 1 : -1));
      } else {
        sortedPartners = partners.sort((a: PartnerResponse, b: PartnerResponse) =>
          a.projects && b.projects && a.projects?.length < b.projects?.length ? 1 : -1
        );
      }
    } else {
      if (sortColumn.name === "name") {
        sortedPartners = partners.sort((a: PartnerResponse, b: PartnerResponse) => (a.name && b.name && a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));
      } else {
        sortedPartners = partners.sort((a: PartnerResponse, b: PartnerResponse) =>
          a.projects && b.projects && a.projects?.length > b.projects?.length ? 1 : -1
        );
      }
    }
    setPartnerStats(sortedPartners);
    setLoading(false);
  };

  useEffect(() => {
    if (loading) {
      setHeaderMenuActive(false);
      PartnersService.get()
        .then((response) => {
          if (response?.records) {
            const sortedPartners = response.records.sort((a: PartnerResponse, b: PartnerResponse) =>
              a.projects && b.projects && a.projects?.length < b.projects?.length ? 1 : -1
            );
            setPartnerStats(sortedPartners);
          }
        })
        .catch((e: ApiResult) => {
          if (!modalData?.content?.contentSet) {
            modalData?.triggerError(e);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [loading, modalData]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!headerMenuRef.current[0]?.contains(event.target as Node) && headerMenuActive) {
        setHeaderMenuActive(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
  }, [headerMenuRef, headerMenuActive]);

  useEffect(() => {
    const regionIds = filteredRegionList ? filteredRegionList : null;
    const cityIds = filteredCityList ? filteredCityList : null;
    const partnerIds = filteredPartnerList ? filteredPartnerList : null;
    const systemIds = filteredSystemList ? filteredSystemList : null;
    const targetGroups = filteredTargetGroupList ? filteredTargetGroupList.map((x: any) => TARGET_GROUPS.getText(x)!) : null;
    const dateFrom = filteredDateFrom ? filteredDateFrom.toDateString() : null;
    const dateTo = filteredDateTo ? filteredDateTo.toDateString() : null;

    PartnersService.getFiltered(partnerIds, regionIds, cityIds, targetGroups, systemIds, dateFrom, dateTo)
      .then((response) => {
        if (response) {
          const sortedPartners = response.sort((a: PartnerResponse, b: PartnerResponse) =>
            a.projects && b.projects && a.projects?.length < b.projects?.length ? 1 : -1
          );
          setPartnerStats(sortedPartners);
        }
      })
      .catch((e: ApiResult) => {
        if (!modalData?.content?.contentSet) {
          modalData?.triggerError(e);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [filteredPartnerList, filteredRegionList, filteredCityList, filteredTargetGroupList, filteredSystemList, filteredDateFrom, filteredDateTo, modalData]);

  return (
    <div className={classNames("u-col-lg-3 u-col-xs-12 u-border-box", projectCSS.listContainer)}>
      {/* Mobile statistics HTML */}
      <div className={classNames("u-flex-column", activeTab === PROJECT_TABS.PARTNER_STATISTICS ? "" : "u-none", adminCSS.mobileTabs)}>
        {headerMenuActive && (
          <MobileHeader
            setRef={setHeaderMenuRef}
            defaultSort={sorting}
            headers={headers.filter((head) => head.sortable)}
            onSorting={(column: Column) => {
              sortTable(column);
              setHeaderMenuActive(false);
            }}
          />
        )}
        <div className={classNames(adminCSS.mobileStatWrap)}>
          <table className="u-w100">
            <tbody>
              {!partnerStats.length && !loading && (
                <tr className="u-col-12 u-t-halign-c">
                  <td colSpan={2}>Nema statistike</td>
                </tr>
              )}
              {!loading &&
                partnerStats.map((partner, index) => (
                  <tr className="u-col-12" key={index}>
                    <td className="u-col-7">
                      {partner.name}
                      <br />
                      {partner.zipCode} - {partner.address}
                    </td>
                    <td className="u-col-5 u-t-w700 u-t-align-end">{partner.projects?.length}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Desktop statistics HTML */}
      <div className={classNames("u-w90", "u-bg-white", "u-pos-relative", adminCSS.statsContainer)}>
        <div>
          <h3 className="u-t-primary u-mb20px u-t-uppercase">Statistika partnera</h3>
        </div>
        <div className={classNames(adminCSS.statTableWrap)}>
          <table className="u-w100 u-pr5per">
            <TableHeader defaultSort={{ name: "projects", sortDescending: true }} headers={headers} onSorting={(sortColumn: Column) => sortTable(sortColumn)} />

            <tbody>
              {!partnerStats.length && !loading && (
                <tr className="u-col-12 u-t-halign-c">
                  <td colSpan={2}>Nema statistike</td>
                </tr>
              )}
              {!loading &&
                partnerStats.map((partner, index) => (
                  <tr className="u-col-12" key={index}>
                    <td className="u-col-7">
                      {partner.name}
                      <br />
                      {partner.zipCode} - {partner.address}
                    </td>
                    <td className="u-col-5 u-t-w700 u-t-align-end">{partner.projects?.length}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        {loading && <Loader />}
      </div>
    </div>
  );
};

export default Statistics;
